import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, Calendar, Share } from "@phosphor-icons/react";
import { toast } from "sonner";
import useLenis from "../lib/useLenis";
import { initAnalytics } from "../lib/analytics";
import SEO from "../components/SEO";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollProgress from "../components/ScrollProgress";
import CookieBanner from "../components/CookieBanner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

function resolveCover(url) {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${BACKEND}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function BlogArticle() {
    useLenis();
    const { slug } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        initAnalytics();
        window.scrollTo(0, 0);
        setLoading(true);
        axios
            .get(`${API}/blog/${slug}`)
            .then(({ data }) => {
                setArticle(data);
                // Fetch related (same category, excluding this one)
                return axios.get(`${API}/blog?category=${encodeURIComponent(data.category)}`);
            })
            .then(({ data }) => {
                setRelated(data.filter((a) => a.slug !== slug).slice(0, 3));
            })
            .catch(() => setArticle(null))
            .finally(() => setLoading(false));
    }, [slug]);

    const onShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: article?.title,
                    text: article?.excerpt,
                    url,
                });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Lien copié dans le presse-papiers.");
            }
        } catch {
            /* user cancelled */
        }
    };

    if (loading) {
        return (
            <main className="bg-scd text-cream min-h-screen flex items-center justify-center">
                <span className="label-eyebrow text-cream/50">Chargement…</span>
            </main>
        );
    }
    if (!article) {
        return (
            <main className="bg-scd text-cream min-h-screen flex flex-col items-center justify-center gap-4">
                <h1 className="font-display text-3xl">Article introuvable</h1>
                <button
                    onClick={() => navigate("/blog")}
                    className="px-5 py-2 rounded-full bg-yellow text-[#272727]"
                >
                    Retour au journal
                </button>
            </main>
        );
    }

    const cover = resolveCover(article.cover_url);
    const pubDate = article.published_at || article.created_at;
    const isoDate = pubDate ? new Date(pubDate).toISOString() : "";
    const humanDate = pubDate
        ? new Date(pubDate).toLocaleDateString("fr-CA", {
              year: "numeric",
              month: "long",
              day: "numeric",
          })
        : "";

    // JSON-LD Article schema for rich results
    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: article.title,
        description: article.excerpt,
        author: { "@type": "Organization", name: article.author },
        datePublished: isoDate,
        image: cover || undefined,
        articleSection: article.category,
        keywords: (article.tags || []).join(", "),
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://centdouze.ca/blog/${article.slug}`,
        },
        publisher: {
            "@type": "Organization",
            name: "Studio Cent Douze",
            logo: {
                "@type": "ImageObject",
                url: "https://customer-assets.emergentagent.com/job_brand-builders-43/artifacts/6hieg182_Artboard%2010.svg",
            },
        },
    };

    return (
        <main data-testid="blog-article" className="bg-scd text-cream">
            <SEO
                title={article.title}
                description={article.excerpt || undefined}
                path={`/blog/${article.slug}`}
                type="article"
                ogImageUrl={cover || undefined}
            />
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(articleSchema)}
                </script>
            </Helmet>
            <ScrollProgress />
            <Header />

            <article className="pt-32 md:pt-40 px-6 md:px-12 lg:px-16">
                <div className="max-w-3xl mx-auto">
                    <Link
                        to="/blog"
                        data-testid="back-to-blog"
                        className="inline-flex items-center gap-2 text-cream/70 hover:text-yellow text-sm transition-colors mb-8"
                    >
                        <ArrowLeft size={14} weight="bold" />
                        Journal
                    </Link>

                    <span className="label-eyebrow text-yellow">
                        — {article.category}
                    </span>
                    <h1
                        className="mt-3 font-display font-semibold text-cream leading-[0.95] tracking-tight"
                        style={{
                            fontSize: "clamp(2.25rem, 5.5vw, 4rem)",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {article.title}
                    </h1>
                    {article.excerpt && (
                        <p className="mt-5 text-lg text-cream/75 leading-relaxed">
                            {article.excerpt}
                        </p>
                    )}

                    <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-cream/60">
                        <span className="flex items-center gap-1.5">
                            <Calendar size={14} weight="duotone" />
                            <time dateTime={isoDate}>{humanDate}</time>
                        </span>
                        <span className="flex items-center gap-1.5">
                            <Clock size={14} weight="duotone" />
                            {article.read_minutes} min de lecture
                        </span>
                        <span>Par {article.author}</span>
                        <button
                            onClick={onShare}
                            data-testid="share-button"
                            className="ml-auto inline-flex items-center gap-1.5 text-cream/70 hover:text-yellow transition-colors"
                        >
                            <Share size={14} weight="bold" />
                            Partager
                        </button>
                    </div>
                </div>

                {cover && (
                    <div className="mt-12 max-w-5xl mx-auto">
                        <img
                            src={cover}
                            alt={article.title}
                            className="w-full h-auto rounded-sm border border-scd"
                        />
                    </div>
                )}

                <div className="mt-14 max-w-3xl mx-auto pb-24">
                    <div className="scd-prose">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {article.content_md}
                        </ReactMarkdown>
                    </div>

                    {article.tags && article.tags.length > 0 && (
                        <div className="mt-12 pt-6 border-t border-scd flex flex-wrap gap-2">
                            {article.tags.map((t) => (
                                <span
                                    key={t}
                                    className="px-3 py-1 rounded-full border border-scd text-xs text-cream/70"
                                >
                                    #{t}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </article>

            {/* Related articles */}
            {related.length > 0 && (
                <section className="border-t border-scd px-6 md:px-12 lg:px-16 py-20">
                    <div className="max-w-6xl mx-auto">
                        <span className="label-eyebrow text-yellow">
                            — À lire aussi
                        </span>
                        <div className="mt-8 grid md:grid-cols-3 gap-6">
                            {related.map((a) => (
                                <Link
                                    key={a.id}
                                    to={`/blog/${a.slug}`}
                                    className="border border-scd bg-elev rounded-sm p-6 hover:border-yellow transition-colors"
                                >
                                    <span className="text-[10px] uppercase tracking-wider text-cream/50">
                                        {a.category}
                                    </span>
                                    <h3 className="mt-2 font-display text-lg text-cream leading-snug">
                                        {a.title}
                                    </h3>
                                    <span className="mt-4 inline-flex items-center gap-1 text-xs text-cream/60">
                                        <Clock size={12} weight="duotone" />
                                        {a.read_minutes} min
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <Footer />
            <CookieBanner />
        </main>
    );
}
